import { ExecStepFile, ReviewFileRef } from '../interfaces/exec.interface';

/**
 * An author-selected review file, paired with the size of the matching job
 * file when one has loaded (EHQ-134).
 */
export interface ReviewFileRow {
  ref: ReviewFileRef;
  /** Null for an unresolved ref, or before the job's file list has loaded. */
  filesize: number | null;
}

/**
 * Pair each ref the author named with its size from the job's file list.
 *
 * The ref carries only a kind, an id and a name — enough to render and to
 * download. Size is a nicety, so a ref with no match still produces a row:
 * the list must render before (and even without) the job's files loading.
 */
export function reviewFileRows(
  refs: ReviewFileRef[] | null | undefined,
  jobFiles: ExecStepFile[] | null | undefined
): ReviewFileRow[] | null {
  if (!refs || refs.length === 0) return null;
  const byId = new Map((jobFiles ?? []).map((file) => [file.fileId, file]));
  return refs.map((ref) => ({
    ref,
    filesize: ref.kind === 'jobFile' ? (byId.get(ref.fileId)?.filesize ?? null) : null,
  }));
}

/**
 * Coerce the optional fileNames argument of requestReview() into a clean list
 * of names (EHQ-134).
 *
 * Mirrors normalizeReviewFileNames in enoch's routine.service.ts — the two must
 * agree on what counts as a usable name, or an author validating a workflow in
 * the browser gets different feedback than the job they eventually run.
 *
 * Python passes this through Pyodide, so a list can arrive as a proxy or as an
 * already-converted array; a caller may also pass a single string. Anything
 * that is not a usable name is dropped rather than throwing — a malformed file
 * list must not turn a legitimate review request into a failed step.
 *
 * Returns undefined when nothing usable was supplied, which the read surfaces
 * treat the same as "no list": show all of the job's files.
 */
export function normalizeReviewFileNames(fileNames: unknown): string[] | undefined {
  if (fileNames === undefined || fileNames === null) return undefined;

  // A bare string is a reasonable thing to pass for a single file.
  const raw =
    typeof fileNames === 'string'
      ? [fileNames]
      : Array.isArray(fileNames)
        ? fileNames
        : // Pyodide list proxies expose toJs(); fall back to iteration.
          typeof (fileNames as any)?.toJs === 'function'
          ? (fileNames as any).toJs()
          : typeof (fileNames as any)?.[Symbol.iterator] === 'function'
            ? [...(fileNames as any)]
            : [];

  const names = (Array.isArray(raw) ? raw : [])
    .filter((n: unknown) => typeof n === 'string')
    .map((n: string) => n.trim())
    .filter((n: string) => n.length > 0);

  return names.length > 0 ? names : undefined;
}
